import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { WMSStatCard } from '@/modules/wms/components/dashboard/WMSStatCard';
import { RealTimeAlertPanel } from '@/modules/wms/components/common/RealTimeAlertPanel';
import { reportsAPI } from '@/shared/services/api';
import {
  BarChart3,
  Download,
  Calendar,
  TrendingUp,
  Users,
  Package,
  Truck,
  Clock,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
  CheckCircle2,
  Loader2 } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend } from
'recharts';
import { toast } from 'sonner';

const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE'];

export const WMSReports = () => {const { t } = useTranslation("wms");
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [throughputData, setThroughputData] = useState(null);
  const [laborData, setLaborData] = useState([]);
  const [spaceData, setSpaceData] = useState(null);
  const [accuracyData, setAccuracyData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [throughput, labor, space, accuracy] = await Promise.all([
        reportsAPI.getThroughput(dateRange),
        reportsAPI.getLaborProductivity(dateRange),
        reportsAPI.getSpaceUtilization(),
        reportsAPI.getOrderAccuracy()]
        );
        setThroughputData(throughput);
        setLaborData(labor || []);
        setSpaceData(space);
        setAccuracyData(accuracy);
      } catch (error) {
        console.error('Failed to fetch reports:', error);
        toast.error('Failed to load reports data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  const handleExport = async (reportType) => {
    try {
      const result = await reportsAPI.exportReport(reportType, 'csv');
      toast.success('Report Generated', { description: `Download ready: ${result.download_url}` });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to generate report');
    }
  };

  const throughput = throughputData;
  const spaceUtil = spaceData;
  const accuracy = accuracyData;

  return (
    <div className="space-y-6" data-testid="wms-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("wmsReportsAnalytics", "WMS Reports & Analytics")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("laborProductivitySpace", "Labor productivity, space utilization, and operational metrics")}

          </p>
        </div>
        <div className="flex gap-2">
          <select
            className="h-10 px-3 border border-input rounded-md bg-background text-sm"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}>
            
            <option value="7d">{t("last7Days", "Last 7 Days")}</option>
            <option value="30d">{t("last30Days", "Last 30 Days")}</option>
            <option value="90d">{t("last90Days", "Last 90 Days")}</option>
            <option value="ytd">{t("yearToDate", "Year to Date")}</option>
          </select>
          <Button variant="outline" className="gap-2" onClick={() => handleExport('wms-summary')}>
            <Download className="h-4 w-4" />{t("exportReport", "Export Report")}

          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {loading ?
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) =>
        <Card key={i} className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-20">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
        )}
        </div> :

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <WMSStatCard
          title="Units Processed Today"
          value={throughput?.daily ? throughput.daily.unitsReceived + throughput.daily.unitsShipped : 0}
          icon={Package}
          color="blue"
          trend={{ type: 'up', value: '+12% vs yesterday' }} />
        
          <WMSStatCard
          title="Order Accuracy"
          value={`${accuracy?.overall || 0}%`}
          icon={Target}
          color="green"
          trend={{ type: 'up', value: '+0.3% this week' }} />
        
          <WMSStatCard
          title="Space Utilization"
          value={`${spaceUtil?.utilizationPercent || 0}%`}
          icon={Layers}
          color="purple"
          subtitle={spaceUtil ? `${spaceUtil.currentUsage.toLocaleString()} / ${spaceUtil.totalCapacity.toLocaleString()} units` : ''} />
        
          <WMSStatCard
          title="Avg Order Time"
          value={`${throughput?.daily?.avgOrderTime || 0} min`}
          icon={Clock}
          color="yellow"
          trend={{ type: 'down', value: '-2 min vs last week' }} />
        
        </div>
      }

      {/* Real-Time Alerts Panel */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="card-elevated h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />{t("throughputMetrics", "Throughput Metrics")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  <p className="text-sm text-muted-foreground">{t("daily", "Daily")}</p>
                  <p className="text-2xl font-bold text-blue-600">{throughput?.daily?.ordersProcessed || 0}</p>
                  <p className="text-xs text-muted-foreground">{t("orders", "orders")}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  <p className="text-sm text-muted-foreground">{t("weekly", "Weekly")}</p>
                  <p className="text-2xl font-bold text-blue-600">{throughput?.weekly?.ordersProcessed || 0}</p>
                  <p className="text-xs text-muted-foreground">{t("orders", "orders")}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 text-center">
                  <p className="text-sm text-muted-foreground">{t("monthly", "Monthly")}</p>
                  <p className="text-2xl font-bold text-blue-600">{throughput?.monthly?.ordersProcessed?.toLocaleString() || 0}</p>
                  <p className="text-xs text-muted-foreground">{t("orders", "orders")}</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={laborData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(val) => val.slice(5)} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} />
                  
                  <Area type="monotone" dataKey="receiving" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} name="Receiving" />
                  <Area type="monotone" dataKey="shipping" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Shipping" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        <RealTimeAlertPanel maxAlerts={4} />
      </div>

      {/* Tabs for Different Reports */}
      <Tabs defaultValue="labor" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="labor" className="gap-2">
            <Users className="h-4 w-4" />{t("laborProductivity", "Labor Productivity")}

          </TabsTrigger>
          <TabsTrigger value="space" className="gap-2">
            <Layers className="h-4 w-4" />{t("spaceUtilization", "Space Utilization")}

          </TabsTrigger>
          <TabsTrigger value="accuracy" className="gap-2">
            <Target className="h-4 w-4" />{t("orderAccuracy", "Order Accuracy")}

          </TabsTrigger>
        </TabsList>

        {/* Labor Productivity Tab */}
        <TabsContent value="labor" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("unitsProcessedBy", "Units Processed by Activity")}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={laborData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(val) => val.slice(5)} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} />
                    
                    <Legend />
                    <Bar dataKey="receiving" name="Receiving" fill="#22c55e" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="putaway" name="Put-away" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="picking" name="Picking" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="packing" name="Packing" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="shipping" name="Shipping" fill="#ef4444" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("productivitySummary", "Productivity Summary")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                  { label: 'Receiving', value: 155, target: 150, color: '#22c55e' },
                  { label: 'Put-away', value: 140, target: 130, color: '#3b82f6' },
                  { label: 'Picking', value: 390, target: 400, color: '#f59e0b' },
                  { label: 'Packing', value: 320, target: 300, color: '#8b5cf6' },
                  { label: 'Shipping', value: 195, target: 200, color: '#ef4444' }].
                  map((item) => {
                    const percentage = item.value / item.target * 100;
                    const isAbove = percentage >= 100;
                    return (
                      <div key={item.label} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{item.label}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{item.value}</span>
                            <span className="text-xs text-muted-foreground">/ {item.target}{t("target", "target")}</span>
                            {isAbove ?
                            <ArrowUpRight className="h-4 w-4 text-green-600" /> :

                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                            }
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${Math.min(percentage, 100)}%`,
                              backgroundColor: item.color
                            }} />
                          
                        </div>
                      </div>);

                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Space Utilization Tab */}
        <TabsContent value="space" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("utilizationByZone", "Utilization by Zone")}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={spaceUtil?.byZone || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis dataKey="zone" type="category" stroke="hsl(var(--muted-foreground))" fontSize={11} width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value}%`, 'Utilization']} />
                    
                    <Bar
                      dataKey="percent"
                      fill="#3b82f6"
                      radius={[0, 4, 4, 0]}
                      label={{ position: 'right', fontSize: 12, fill: 'hsl(var(--foreground))' }} />
                    
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("utilizationTrend", "Utilization Trend")}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={spaceUtil?.trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis domain={[50, 80]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} />
                    
                    <Line
                      type="monotone"
                      dataKey="utilization"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                      name="Utilization %" />
                    
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Order Accuracy Tab */}
        <TabsContent value="accuracy" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("weeklyAccuracyTrend", "Weekly Accuracy Trend")}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={accuracy?.byWeek || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis domain={[95, 100]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} />
                    
                    <Bar dataKey="accuracy" name="Accuracy %" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("errorTypesBreakdown", "Error Types Breakdown")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={accuracy?.errorTypes || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={4}
                        dataKey="count"
                        nameKey="type">
                        
                        {(accuracy?.errorTypes || []).map((entry, index) =>
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        )}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }} />
                      
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {(accuracy?.errorTypes || []).map((item, index) =>
                  <div key={item.type} className="flex items-center gap-2 text-sm">
                      <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                    
                      <span className="text-muted-foreground">{item.type}</span>
                      <span className="font-medium ml-auto">{item.count}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>);

};